import { useState } from "react";
import { NavLink } from "react-router-dom";
// Chakra imports
import {
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    Input,
    Text,
    useColorModeValue,
    useToast
} from "@chakra-ui/react";
// Custom components
import DefaultAuth from "layouts/auth/Default";
// Assets
import illustration from "assets/img/auth/mysportset.png";
import { RootPath } from "routes/types";
import { translate } from "../../i18n";
import { useAuth } from "./AuthProvider";
import { useSendPasswordResetEmail } from "./hooks/useSendPasswordResetEmail";
import RedirectAuthorizedUser from "./RedirectAuthorizedUser";

function ForgotPasswordPage() {
    const [email, setEmail] = useState('');
    const { currentUser, } = useAuth();
    const toast = useToast();
    const { mutateAsync: sendResetPasswordEmail, isLoading: isResetting } = useSendPasswordResetEmail(() => {
        toast({
            title: translate('auth.forgot_password_success'),
            status: 'success',
            duration: 4000,
        })
    });

    const handleSubmit = async () => {
        try {
            await sendResetPasswordEmail(email);
        } catch {
            toast({
                title: translate('auth.forgot_password_error'),
                status: 'error',
                duration: 4000,
            });
        }
    };

    // Chakra color mode
    const textColor = useColorModeValue("navy.700", "white");
    const textColorSecondary = useColorModeValue("gray.400", "gray.500");
    const textColorDetails = useColorModeValue("navy.700", "secondaryGray.600");
    const textColorBrand = useColorModeValue("brand.500", "white");
    const brandStars = useColorModeValue("brand.500", "brand.400");
    if (currentUser) {
        return <RedirectAuthorizedUser user={currentUser} />
    }

    return (
        <DefaultAuth illustrationBackground={illustration} image={illustration}>
            <Flex
                maxW={{ base: "100%", md: "max-content" }}
                w='100%'
                mx={{ base: "auto", lg: "0px" }}
                me='auto'
                h='100%'
                alignItems='start'
                justifyContent='center'
                mb={{ base: "30px", md: "60px" }}
                px={{ base: "25px", md: "0px" }}
                mt={{ base: "40px", md: "14vh" }}
                flexDirection='column'>
                <Box me='auto'>
                    <Heading color={textColor} fontSize='36px' mb='10px'>
                        {translate('auth.forgot_password')}
                    </Heading>
                    <Text
                        mb='36px'
                        ms='4px'
                        color={textColorSecondary}
                        fontWeight='400'
                        fontSize='md'>
                        {translate('auth.forgot_password_description')}
                    </Text>
                </Box>
                <Flex
                    zIndex='2'
                    direction='column'
                    w={{ base: "100%", md: "420px" }}
                    maxW='100%'
                    background='transparent'
                    borderRadius='15px'
                    mx={{ base: "auto", lg: "unset" }}
                    me='auto'
                    mb={{ base: "20px", md: "auto" }}>
                    <FormControl>
                        <FormLabel
                            display='flex'
                            ms='4px'
                            fontSize='sm'
                            fontWeight='500'
                            color={textColor}
                            mb='8px'>
                            {translate('auth.email')}<Text color={brandStars}>*</Text>
                        </FormLabel>
                        <Input
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            isRequired={true}
                            variant='auth'
                            fontSize='sm'
                            ms={{ base: "0px", md: "0px" }}
                            type='email'
                            placeholder='jean@dujardin.com'
                            mb='24px'
                            fontWeight='500'
                            size='lg'
                        />
                        <Button
                            isLoading={isResetting}
                            fontSize='sm'
                            variant='brand'
                            fontWeight='500'
                            w='100%'
                            h='50'
                            onClick={handleSubmit}
                            mb='24px'>
                            {translate('auth.forgot_password_cta')}
                        </Button>
                    </FormControl>
                    <Flex
                        flexDirection='column'
                        justifyContent='center'
                        alignItems='start'
                        maxW='100%'
                        mt='0px'>
                        <Text color={textColorDetails} fontWeight='400' fontSize='14px'>
                            <NavLink to={RootPath.AUTH}>
                                <Text
                                    color={textColorBrand}
                                    as='span'
                                    ms='5px'
                                    fontWeight='500'>
                                    {translate('auth.back_to_signin')}
                                </Text>
                            </NavLink>
                        </Text>
                    </Flex>
                </Flex>
            </Flex>
        </DefaultAuth>
    );
}

export default ForgotPasswordPage;
